<template>
  <div class="audio-page">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <h1 class="brand-name">树苗英语</h1>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id && item.id !== 'logout' }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </nav>
      </aside>

      <main class="audio-shell">
      <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
        <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
          <font-awesome-icon icon="chevron-left" />
        </button>
        <h1 class="mobile-topbar-title">{{ mobilePageTitle }}</h1>
        <span class="mobile-topbar-spacer" aria-hidden="true"></span>
      </div>
      <div class="mobile-topbar-offset" aria-hidden="true"></div>

      <header class="audio-header">
        <button type="button" class="back-btn" @click="handleGoBack" aria-label="返回上一页">
          <font-awesome-icon icon="chevron-left" />
        </button>
      </header>

      <nav class="audio-tabs" aria-label="音频分类">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="audio-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </nav>

      <section class="audio-list" aria-label="音频列表">
        <article
          v-for="item in visibleAudioItems"
          :key="item.id"
          class="audio-item"
          :class="{ playing: currentAudioItemId === item.id }"
          @click="playAudio(item)"
        >
          <div class="audio-item-icon">
            <font-awesome-icon icon="headphones" />
          </div>
          <div class="audio-item-copy">
            <h2 class="audio-title">{{ item.title }}</h2>
            <p class="audio-date">{{ item.date }}</p>
          </div>
          <button
            type="button"
            class="favorite-btn"
            :class="{ active: item.isFavorite }"
            :aria-label="item.isFavorite ? '取消收藏' : '收藏'"
            @click.stop="toggleFavorite(item.id)"
          >
            <font-awesome-icon :icon="item.isFavorite ? 'heart-circle-check' : 'heart'" />
            <span>{{ item.isFavorite ? '已收藏' : '收藏' }}</span>
          </button>
        </article>

        <div v-if="visibleAudioItems.length === 0" class="empty-state">
          <font-awesome-icon icon="headphones" />
          <p>没有收藏内容</p>
        </div>
      </section>

      <Transition name="player-dock">
        <section v-if="currentAudioTitle" class="audio-player-dock" aria-label="底部音频播放器">
          <div class="audio-player-head">
            <div class="audio-player-now">
              <span class="audio-player-label">正在播放</span>
              <strong class="audio-player-title">{{ currentAudioTitle }}</strong>
            </div>
          </div>
          <audio
            ref="audioPlayerRef"
            class="audio-player"
            :src="testAudioSrc"
            controls
            preload="metadata"
            @ended="handleAudioEnded"
          ></audio>
        </section>
      </Transition>
      </main>
    </div>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup>
import logo2Image from '../assets/logo2.png'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import testAudioSrc from '../assets/test.mp3'

const route = useRoute()
const router = useRouter()

const tabs = [
  { id: 'all', label: '全部' },
  { id: 'favorites', label: '收藏' },
  { id: 'recent', label: '最近' }
]

const activeTab = ref('all')
const activeNav = ref('')
const isTopbarScrolled = ref(false)
const currentAudioItemId = ref('')
const currentAudioTitle = ref('')
const audioPlayerRef = ref(null)
const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const parentPasswordModalRef = ref(null)
const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

navItems.push({ id: 'logout', label: '登出账号', icon: 'right-from-bracket' })
const mobilePageTitle = computed(() => {
  const title = typeof route.query.title === 'string' ? route.query.title.trim() : ''
  return title || '音频库'
})

const audioItems = ref([
  {
    id: 'audio-1',
    title: 'Good Morning Song 晨间律动儿歌',
    date: '2026-05-02',
    icon: 'music',
    isFavorite: true,
    isRecent: true
  },
  {
    id: 'audio-2',
    title: 'Bedtime Lullaby 睡前安抚儿歌',
    date: '2026-04-29',
    icon: 'moon',
    isFavorite: false,
    isRecent: true
  },
  {
    id: 'audio-3',
    title: 'Peppa Pig 音频故事 Early Bird',
    date: '2026-04-24',
    icon: 'book-open',
    isFavorite: true,
    isRecent: false
  },
  {
    id: 'audio-4',
    title: 'Alphabet Chant 字母节奏磨耳朵',
    date: '2026-04-18',
    icon: 'headphones',
    isFavorite: false,
    isRecent: false
  },
  {
    id: 'audio-5',
    title: 'Magic School Bus 有声书 The Solar System',
    date: '2026-04-11',
    icon: 'rocket',
    isFavorite: true,
    isRecent: false
  },
  {
    id: 'audio-6',
    title: 'Animal Friends Story Time 动物朋友故事',
    date: '2026-04-06',
    icon: 'paw',
    isFavorite: false,
    isRecent: true
  }
])

const visibleAudioItems = computed(() => {
  if (activeTab.value === 'favorites') {
    return audioItems.value.filter((item) => item.isFavorite)
  }

  if (activeTab.value === 'recent') {
    return audioItems.value.filter((item) => item.isRecent)
  }

  return audioItems.value
})

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const handleLogout = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(PARENT_VERIFIED_STORAGE_KEY)
  router.replace('/login')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'logout') {
    handleLogout()
    return
  }

  if (item.id === 'home') {
    router.push('/home')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const toggleFavorite = (itemId) => {
  const target = audioItems.value.find((item) => item.id === itemId)

  if (!target) {
    return
  }

  target.isFavorite = !target.isFavorite
}

const playAudio = (item) => {
  currentAudioItemId.value = item.id
  currentAudioTitle.value = item.title

  requestAnimationFrame(() => {
    if (!audioPlayerRef.value) {
      return
    }

    audioPlayerRef.value.currentTime = 0
    audioPlayerRef.value.play().catch(() => {})
  })
}

const handleAudioEnded = () => {
  const queue = visibleAudioItems.value
  const currentIndex = queue.findIndex((item) => item.id === currentAudioItemId.value)

  if (currentIndex === -1) {
    return
  }

  const nextItem = queue[currentIndex + 1]

  if (!nextItem) {
    return
  }

  playAudio(nextItem)
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 2
}

onMounted(() => {
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
})
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.audio-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #fffaf5 0%, #f8f3ec 52%, #f3eee7 100%);
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(90px);
  opacity: 0.55;
}

.orb-1 {
  width: 320px;
  height: 320px;
  top: -100px;
  left: -80px;
  background: rgba(255, 170, 102, 0.42);
}

.orb-2 {
  width: 360px;
  height: 360px;
  right: -100px;
  top: 120px;
  background: rgba(112, 200, 180, 0.26);
}

.orb-3 {
  width: 280px;
  height: 280px;
  bottom: -80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(120, 164, 255, 0.22);
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  height: 100vh;
  overflow-y: auto;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 2;
}

.nav-card {
  min-height: 100%;
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 48px;
  height: 48px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.nav-logo .brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 2px;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px 12px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.audio-shell {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  width: min(100%, 860px);
  margin: 0 auto;
  padding: 36px 20px 164px;
}

.audio-header {
  position: relative;
  min-height: 64px;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.back-btn {
  display: none;
  position: absolute;
  top: 8px;
  right: 0;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.75);
  color: #5f5044;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(45, 52, 54, 0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

.back-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(45, 52, 54, 0.11);
}

.audio-tabs {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.audio-tab {
  min-width: 104px;
  min-height: 46px;
  padding: 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.62);
  color: #7b6a5f;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(70, 59, 47, 0.05);
  transition: transform 0.22s ease, background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
}

.audio-tab:hover {
  transform: translateY(-1px);
}

.audio-tab.active {
  background: linear-gradient(135deg, #ff9d56 0%, #ff7a33 100%);
  color: #ffffff;
  box-shadow: 0 14px 30px rgba(255, 125, 48, 0.22);
}

.audio-list {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.audio-item {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.76);
  border: 1.5px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    0 16px 34px rgba(95, 74, 52, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.audio-item:hover {
  transform: translateY(-2px);
  box-shadow:
    0 20px 38px rgba(95, 74, 52, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
}

.audio-item.playing {
  transform: scale(1.02);
  border-color: #ff8c42;
  box-shadow: 0 18px 42px rgba(255, 140, 66, 0.22);
  animation: audio-playing-pulse 1s ease-in-out infinite;
}

.audio-item.playing .audio-title {
  color: #ff8c42;
}

.audio-item.playing .audio-item-icon {
  transform: scale(1.2);
}

.audio-item-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex-shrink: 0;
  background: #fdf0e3;
  color: #ff8a3d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86);
  transition: transform 0.22s ease;
}

.audio-item-copy {
  min-width: 0;
  flex: 1;
}

.audio-title {
  margin: 0;
  color: #2d3436;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
  transition: color 0.22s ease;
}

.audio-date {
  margin: 8px 0 0;
  color: #9b8878;
  font-size: 13px;
  font-weight: 700;
}

.favorite-btn {
  flex-shrink: 0;
  min-width: 92px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid rgba(255, 173, 111, 0.4);
  border-radius: 999px;
  background: rgba(255, 247, 239, 0.96);
  color: #d9782f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.favorite-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(217, 120, 47, 0.14);
}

.favorite-btn.active {
  border-color: rgba(255, 143, 66, 0.42);
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #ffffff;
  box-shadow: 0 12px 24px rgba(255, 132, 56, 0.2);
}

.audio-player-dock {
  position: fixed;
  left: calc(280px + (100vw - 280px) / 2);
  bottom: 18px;
  transform: translateX(-50%);
  z-index: 30;
  width: min(calc(100vw - 304px), 860px);
  padding: 14px 16px 16px;
  border-radius: 24px;
  background: rgba(255, 250, 245, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow:
    0 18px 40px rgba(95, 74, 52, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.audio-player-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.audio-player-now {
  min-width: 0;
}

.audio-player-label {
  display: block;
  color: #d27a33;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
}

.audio-player-title {
  display: block;
  margin-top: 4px;
  color: #2d3436;
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-player {
  width: 100%;
  height: 42px;
}

@keyframes audio-playing-pulse {
  0%,
  100% {
    box-shadow: 0 18px 42px rgba(255, 140, 66, 0.22);
  }

  50% {
    box-shadow: 0 16px 36px rgba(255, 140, 66, 0.16);
  }
}

.player-dock-enter-active,
.player-dock-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.player-dock-enter-from,
.player-dock-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

.empty-state {
  padding: 48px 20px;
  border-radius: 28px;
  text-align: center;
  color: #9b8878;
  background: rgba(255, 255, 255, 0.58);
  border: 1px dashed rgba(216, 173, 134, 0.5);
}

.empty-state svg {
  font-size: 30px;
  color: #ff8a3d;
}

.empty-state p {
  margin: 14px 0 0;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 992px) {
  .sidebar {
    width: 236px;
  }

  .nav-logo {
    padding: 20px 16px;
    gap: 10px;
  }

  .nav-logo .logo-icon {
    width: 42px;
    height: 42px;
    font-size: 20px;
  }

  .nav-logo .brand-name {
    font-size: 18px;
  }

  .nav-item {
    padding: 16px;
  }

  .audio-player-dock {
    left: calc(236px + (100vw - 236px) / 2);
    width: min(calc(100vw - 260px), 860px);
  }
}

@media (max-width: 768px) {
  .audio-page {
    overflow-y: auto;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
  }

  .sidebar {
    display: none;
  }

  .audio-shell {
    width: 100%;
    padding: 18px 14px 158px;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .back-btn {
    display: none;
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .audio-header {
    min-height: 0;
  }

  .audio-tabs {
    gap: 10px;
  }

  .audio-tab {
    min-width: 90px;
    min-height: 42px;
    padding: 0 18px;
    font-size: 14px;
  }

  .audio-item {
    gap: 14px;
    padding: 16px;
    border-radius: 20px;
  }

  .audio-item-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    font-size: 21px;
  }

  .audio-title {
    font-size: 16px;
  }

  .favorite-btn {
    min-width: 78px;
    min-height: 36px;
    padding: 0 12px;
    gap: 6px;
    font-size: 12px;
  }

  .audio-player-dock {
    left: 50%;
    width: calc(100vw - 16px);
    bottom: 10px;
    padding: 12px 12px 14px;
    border-radius: 20px;
  }

  .audio-player-title {
    font-size: 14px;
  }
}
</style>
